import React from 'react';
import { Users, Award, Mail } from 'lucide-react';
import { teamMembers } from '../data/mock';
import teamHero from "../assets/images/ourTeamBG.webp";

const Team = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section
        className="relative py-28 bg-cover bg-center"
        style={{
          backgroundImage: `
      linear-gradient(to bottom, rgba(255,247,230,0.85), rgba(192, 119, 51, 0.8)),
      url('${teamHero}')
    `,
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold font-serif">
              Our Team
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              Meet the dedicated minds driving excellence in Indian Knowledge Systems
            </p>
          </div>
        </div>
      </section>

      {/* Patron */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-secondary/30 to-primary/10 p-8 rounded-2xl shadow-xl text-center border-2 border-primary/20">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {teamMembers.patron.name}
              </h2>
              <p className="text-primary font-semibold text-lg mb-2">
                {teamMembers.patron.designation}
              </p>
              <p className="text-muted-foreground">
                {teamMembers.patron.organization}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Leadership Team
              </h2>
              <p className="text-muted-foreground text-lg">
                Strategic vision and guidance
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {teamMembers.leadership.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover-lift"
                >
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-orange-400/20 flex items-center justify-center">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl">
                      <Users className="w-12 h-12 text-primary" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {member.name}
                    </h3>
                    <p className="text-primary font-semibold mb-2">
                      {member.designation}
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      {member.role}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {member.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Core Team Members
              </h2>
              <p className="text-muted-foreground text-lg">
                Interdisciplinary expertise driving research and innovation
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {teamMembers.coreTeam.map((member, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-secondary/20 p-6 rounded-xl shadow-lg hover-lift text-center border border-primary/10"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2 text-sm">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary font-medium">
                    {member.department}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground italic">
                Additional members from allied disciplines will be invited
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advisory Board */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Advisory Board
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Our Advisory Board comprises eminent academicians, researchers, scientists, philosophers, and cultural leaders from India and abroad, providing strategic guidance for research, collaborations, and institutional growth.
              </p>
              <div className="mt-6 text-sm text-primary font-semibold">
                Members to be announced
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold font-serif">
              Join Our Academic Community
            </h2>
            <p className="text-xl text-white/90">
              Interested in collaborating or joining our team?
            </p>
            <a
              href="mailto:vaciks@maitreyi.du.ac.in"
              className="inline-flex items-center gap-2 bg-white text-primary font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-xl"
            >
              <Mail className="w-5 h-5" />
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
